// src/pages/Services.tsx — every service, grouped by its live category.
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useServices } from "../lib/context";
import {
  MIN_VISIT_EXEMPT_LABEL, ops, primaryEconomics, REGION_LABEL, TIER_LABEL, TRACK_LABEL, trackEconomics,
  type Change, type MinVisitExempt, type Region, type Service, type ServiceVariant, type Settings, type StoryEconomics, type Tier,
} from "../lib/ops";
import { marginTone, money, monthsLabel, parseDollars, pct, toDollarText } from "../lib/format";
import { ChangePanel, type Pending } from "../components/ChangePanel";
import { isDraft, offerPreview, SCOPE_MAX, scopeProblems, scopeState, smsSegments, withoutMark, type ScopeState } from "../lib/scope";
import type { Lang } from "../lib/dispatch/dispatch-time";
import { CatalogHealth } from "../components/CatalogHealth";
import { Chip, Field, Loading, Margin, MonthToggles, Notice, PageFrame, SectionLabel } from "../components/ui";

export default function Services() {
  const { data, error, loading, refresh } = useServices();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const [scopeView, setScopeView] = useState<"all" | Exclude<ScopeState, "verified">>("all");   // GM-TXT-02 B3
  const open = params.get("open");
  const scopeCounts = useMemo(() => {
    const c = { missing: 0, draft: 0 };
    for (const s of data?.services ?? []) if (s.active) { const st = scopeState(s); if (st !== "verified") c[st]++; }
    return c;
  }, [data]);

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = new Map<string, Service[]>();
    for (const s of data?.services ?? []) {
      if (q && !`${s.name} ${s.id} ${s.category ?? ""}`.toLowerCase().includes(q)) continue;
      if (scopeView !== "all" && !(s.active && scopeState(s) === scopeView)) continue;
      const cat = s.category ?? "Uncategorized";
      if (!out.has(cat)) out.set(cat, []);
      out.get(cat)!.push(s);
    }
    return Array.from(out.entries());
  }, [data, query, scopeView]);

  // Arriving from another screen with ?open=<id>: scroll that row into view.
  useEffect(() => {
    if (!open || !data) return;
    document.getElementById(`svc-${open}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [open, data]);

  const toggle = (id: string) => {
    const next = new URLSearchParams(params);
    if (open === id) next.delete("open");
    else next.set("open", id);
    setParams(next, { replace: true });
  };

  const panel = pending ? (
    <ChangePanel pending={pending} onClose={() => setPending(null)} onApplied={refresh} />
  ) : undefined;

  return (
    <PageFrame
      title="Services"
      lede={data ? `Prices today (${data.as_of}) at ${data.settings.reference_zips.join(" and ")}. Margin shown at the Oficial rate.` : undefined}
      panel={panel}
    >
      <CatalogHealth />
      <input
        className="search"
        type="search"
        placeholder="Search services by name, id or category"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search services"
      />
      {data && (
        <div className="segmented wrap scope-filter" role="group" aria-label="What the worker sees">
          <button type="button" className={scopeView === "all" ? "seg on" : "seg"} aria-pressed={scopeView === "all"} onClick={() => setScopeView("all")}>
            All services
          </button>
          <button type="button" className={scopeView === "missing" ? "seg on" : "seg"} aria-pressed={scopeView === "missing"} onClick={() => setScopeView("missing")}>
            No scope ({scopeCounts.missing})
          </button>
          <button type="button" className={scopeView === "draft" ? "seg on" : "seg"} aria-pressed={scopeView === "draft"} onClick={() => setScopeView("draft")}>
            Unverified ({scopeCounts.draft})
          </button>
        </div>
      )}
      {loading && !data && <Loading what="services" />}
      {error && <Notice kind="error">{error}</Notice>}
      {data && groups.length === 0 && (
        <p className="empty">
          {scopeView === "missing" ? "Every active service has a scope line." : scopeView === "draft" ? "Every scope line is verified." : `No service matches “${query}”.`}
        </p>
      )}
      {data &&
        groups.map(([cat, list]) => (
          <section key={cat} className="svc-group">
            <SectionLabel>
              {cat} ({list.length})
            </SectionLabel>
            <div className="svc-list">
              {list.map((s) => (
                <ServiceRow
                  key={s.id}
                  s={s}
                  settings={data.settings}
                  open={open === s.id}
                  onToggle={() => toggle(s.id)}
                  onPropose={setPending}
                />
              ))}
            </div>
          </section>
        ))}
    </PageFrame>
  );
}

function statusChips(s: Service) {
  const chips = [];
  if (!s.active) chips.push(<Chip key="off" tone="muted">Inactive</Chip>);
  for (const r of s.inactive_regions ?? []) chips.push(<Chip key={r} tone="warn">Off in {REGION_LABEL[r]}</Chip>);
  if (s.available_months) chips.push(<Chip key="win" tone="info">{monthsLabel(s.available_months)}</Chip>);
  if (s.presale_months) chips.push(<Chip key="pre" tone="info">Pre-order {monthsLabel(s.presale_months)}</Chip>);
  if (!s.seasonal_pricing) chips.push(<Chip key="flat" tone="muted">Flat all year</Chip>);
  if (s.active && scopeState(s) === "missing") chips.push(<Chip key="scope" tone="muted">No scope</Chip>);       // GM-TXT-02 B3
  if (s.active && scopeState(s) === "draft") chips.push(<Chip key="scope" tone="warn">Unverified</Chip>);
  return chips;
}

function ServiceRow(props: {
  s: Service;
  settings: Settings;
  open: boolean;
  onToggle: () => void;
  onPropose: (p: Pending) => void;
}) {
  const { s, settings } = props;
  const econ = primaryEconomics(s);
  const tone = econ ? marginTone(econ.margin_pct.oficial, econ.below_cost, settings.target_margin_pct, settings.margin_floor_pct) : null;
  const formula = econ?.implied_price_cents;
  return (
    <article id={`svc-${s.id}`} className={props.open ? "svc open" : "svc"}>
      <button type="button" className="svc-row" aria-expanded={props.open} onClick={props.onToggle}>
        <span className="svc-name">
          <span className="name">{s.name}</span>
          <span className="id">{s.id}</span>
          <span className="chips">{statusChips(s)}</span>
        </span>
        <span className="svc-base">
          {s.variants?.length ? (
            // SR-B2: priced by track — show every track
            s.variants.map((v) => (
              <span key={v.variant} className="money">
                {TRACK_LABEL[v.variant] ?? v.variant} {money(v.base_price_cents)}
              </span>
            ))
          ) : (
            <span className="money">{s.base_price_cents === null ? "No price" : money(s.base_price_cents)}</span>
          )}
          {!s.variants?.length && formula && s.base_price_cents !== null && formula !== s.base_price_cents && (
            <span className="formula">Formula {money(formula)}</span>
          )}
        </span>
        <span className="svc-zips">
          {s.prices.map((p) => (
            <span key={p.zip} className="zip-price">
              <span className="zip">{p.zip}</span>
              <span className="money">{money(p.price_cents)}</span>
              {p.price_cents !== null && p.available_today === false && <span className="off-today">not sold today</span>}
            </span>
          ))}
        </span>
        <span className="svc-margin">
          {econ ? <Margin value={pct(econ.margin_pct.oficial)} tone={tone} estimated={econ.estimated} /> : <span className="muted">—</span>}
        </span>
      </button>
      {props.open && <ServiceEditor key={JSON.stringify([s, settings])} s={s} settings={settings} onPropose={props.onPropose} />}
    </article>
  );
}

function ServiceEditor({ s, settings, onPropose }: { s: Service; settings: Settings; onPropose: (p: Pending) => void }) {
  // SR-B2: services priced by track (Lit Up) edit their tracks; the database refuses single-price and cost edits
  if (s.variants?.length) {
    return (
      <div className="svc-editor">
        <TracksSection s={s} variants={s.variants} settings={settings} onPropose={onPropose} />
        <StoriesSection s={s} onPropose={onPropose} />
        <ScopeSection s={s} onPropose={onPropose} />
        <AvailabilitySection s={s} onPropose={onPropose} />
        <SeasonalitySection s={s} onPropose={onPropose} />
        <MinimumSection s={s} settings={settings} onPropose={onPropose} />
      </div>
    );
  }
  return (
    <div className="svc-editor">
      <PriceSection s={s} onPropose={onPropose} />
      <StoriesSection s={s} onPropose={onPropose} />
      <ScopeSection s={s} onPropose={onPropose} />
      <CostSection s={s} settings={settings} onPropose={onPropose} />
      <AvailabilitySection s={s} onPropose={onPropose} />
      <SeasonalitySection s={s} onPropose={onPropose} />
      <MinimumSection s={s} settings={settings} onPropose={onPropose} />
      <TierSection s={s} settings={settings} />
    </div>
  );
}

// ── House stories (GM-PRICE-02): price, tarifa and margin at 1 / 2 / 3 stories ──
function StoriesSection({ s, onPropose }: { s: Service; onPropose: (p: Pending) => void }) {
  const [eco, setEco] = useState<StoryEconomics | null | undefined>(undefined);
  const [steps, setSteps] = useState<Record<string, string>>({});
  useEffect(() => {
    let off = false;
    // Fail safe: if the numbers can't load, the section hides and the rest of the editor is unaffected.
    try {
      Promise.resolve(ops.storyEconomics?.(s.id))
        .then((e) => { if (!off) { setEco(e ?? null); setSteps(Object.fromEntries((e?.tracks ?? []).map((t) => [t.variant ?? "", toDollarText(t.story_step_cents ?? null)]))); } })
        .catch(() => { if (!off) setEco(null); });
    } catch { setEco(null); }
    return () => { off = true; };
  }, [s.id, s.base_price_cents]);
  if (!eco) return null;   // service doesn't ask stories (or still loading)
  const tiers: Tier[] = ["aprendiz", "oficial", "maestro"];
  return (
    <section className="editor-section">
      <SectionLabel>House stories</SectionLabel>
      <p className="framing">
        Ladder premium {money(eco.rate_2_cents_per_hr)}/h at 2 stories, {money(eco.rate_3_cents_per_hr)}/h at 3+ (Settings), inside the
        Maestro's tarifa. Prices at {eco.zip}; margin floor {eco.floor_pct}%. Jobs keep the stories they were priced for.
      </p>
      {eco.tracks.map((t) => {
        const key = t.variant ?? "";
        const stepC = parseDollars(steps[key] ?? "");
        return (
          <div key={key} className="track">
            {t.variant && <p className="hint">{TRACK_LABEL[t.variant] ?? t.variant}</p>}
            <div className="table-wrap">
              <table className="data">
                <thead><tr><th>Stories</th><th>Price</th>{tiers.map((x) => <th key={x}>{TIER_LABEL[x]} tarifa · margin</th>)}</tr></thead>
                <tbody>
                  {t.levels.map((l) => (
                    <tr key={l.stories}>
                      <td>{l.stories === 3 ? "3+" : l.stories}</td>
                      <td>{money(l.price_cents)}</td>
                      {tiers.map((x) => {
                        const c = l.by_tier[x];
                        const low = c?.margin_pct !== null && c?.margin_pct !== undefined && c.margin_pct < eco.floor_pct;
                        return <td key={x}>{money(c?.tarifa ?? null)} · <span className={low ? "warn" : undefined}>{c?.margin_pct ?? "—"}%</span></td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {t.variant && (
              <div className="row-actions">
                <Field label="Price step per story ($)" htmlFor={`step-${s.id}-${key}`} hint="Added once at 2 stories and twice at 3+.">
                  <input id={`step-${s.id}-${key}`} inputMode="decimal" value={steps[key] ?? ""} onChange={(e) => setSteps({ ...steps, [key]: e.target.value })} />
                </Field>
                <button type="button" className="btn" disabled={stepC === null || stepC === (t.story_step_cents ?? null)}
                  onClick={() => onPropose({ title: `${s.name} · ${TRACK_LABEL[t.variant!] ?? t.variant} · step ${money(stepC)}`,
                    change: { type: "variant_item", service_id: s.id, variant: t.variant!, story_step_cents: stepC! } })}>
                  Preview change
                </button>
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
}

// ── Price & hours ──────────────────────────────────────────
function PriceSection({ s, onPropose }: { s: Service; onPropose: (p: Pending) => void }) {
  const [price, setPrice] = useState(toDollarText(s.base_price_cents));
  const [rate, setRate] = useState(toDollarText(s.obra_rate_cents));
  const [hours, setHours] = useState(s.estimated_hours === null ? "" : String(s.estimated_hours));

  const priceC = parseDollars(price);
  const rateC = rate.trim() === "" ? null : parseDollars(rate);
  const hoursN = hours.trim() === "" ? null : Number(hours);
  const invalid =
    priceC === null || priceC <= 0 || (rate.trim() !== "" && rateC === null) || (hours.trim() !== "" && !(hoursN! >= 0.25 && hoursN! <= 100));

  const change: Change = { type: "service_price", service_id: s.id };
  if (priceC !== null && priceC !== s.base_price_cents) change.base_price_cents = priceC;
  if (rateC !== null && rateC !== s.obra_rate_cents) change.obra_rate_cents = rateC;
  if (hoursN !== null && hoursN !== s.estimated_hours) change.estimated_hours = hoursN;
  const dirty = Object.keys(change).length > 2;

  return (
    <section className="editor-section">
      <SectionLabel>Price & hours</SectionLabel>
      <div className="fields">
        <Field label="Base price ($)" htmlFor={`p-${s.id}`}>
          <input id={`p-${s.id}`} inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
        </Field>
        <Field label="Standard hours" htmlFor={`h-${s.id}`} hint="Payout = hours × título rate + allowances + trip.">
          <input id={`h-${s.id}`} inputMode="decimal" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="Not set" />
        </Field>
        <Field label="Contractor rate, legacy ($)" htmlFor={`r-${s.id}`} hint="Used only when hours aren't set.">
          <input id={`r-${s.id}`} inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Not set" />
        </Field>
      </div>
      {invalid && <p className="hint warn">Price must be a dollar amount; hours between 0.25 and 100.</p>}
      <button
        type="button"
        className="btn primary"
        disabled={!dirty || invalid}
        onClick={() => onPropose({ change, title: `${s.name}: price & hours` })}
      >
        Preview price change
      </button>
    </section>
  );
}

// ── Costs ──────────────────────────────────────────────────
type TripMode = "global" | "rides" | "custom";

function CostSection({ s, settings, onPropose }: { s: Service; settings: Settings; onPropose: (p: Pending) => void }) {
  const c = s.cost_profile;
  const initialMode: TripMode = !c || c.mobilization_cents === null ? "global" : c.mobilization_cents === 0 ? "rides" : "custom";
  const [kit, setKit] = useState(toDollarText(c?.kit_cost_cents ?? 0));
  const [disposal, setDisposal] = useState(toDollarText(c?.disposal_allowance_cents ?? 0));
  const [equipment, setEquipment] = useState(toDollarText(c?.equipment_allowance_cents ?? 0));
  const [other, setOther] = useState(toDollarText(c?.other_direct_cost_cents ?? 0));
  const [mode, setMode] = useState<TripMode>(initialMode);
  const [trip, setTrip] = useState(initialMode === "custom" ? toDollarText(c?.mobilization_cents ?? 0) : "");
  const [source, setSource] = useState(c?.source ?? "");

  const vals = {
    kit_cost_cents: parseDollars(kit),
    disposal_allowance_cents: parseDollars(disposal),
    equipment_allowance_cents: parseDollars(equipment),
    other_direct_cost_cents: parseDollars(other),
  };
  const tripC: number | null | undefined = mode === "global" ? null : mode === "rides" ? 0 : parseDollars(trip) ?? undefined;
  const invalid = Object.values(vals).some((v) => v === null) || tripC === undefined || (tripC !== null && tripC > 20000);

  const change: Change = { type: "cost_profile", service_id: s.id };
  if (!invalid) {
    for (const [k, v] of Object.entries(vals) as [keyof typeof vals, number][]) {
      if (!c || v !== c[k]) change[k] = v;
    }
    if (!c || tripC !== c.mobilization_cents) change.mobilization_cents = tripC as number | null;
    if (source.trim() && source.trim() !== (c?.source ?? "")) change.source = source.trim();
  }
  const dirty = Object.keys(change).length > 2;

  return (
    <section className="editor-section">
      <SectionLabel>Costs</SectionLabel>
      {!c && <Notice kind="info">No cost profile yet, so margins are estimated at a flat 44%. Saving costs here creates one.</Notice>}
      <div className="fields">
        <Field label="Kit ($)" htmlFor={`k-${s.id}`}>
          <input id={`k-${s.id}`} inputMode="decimal" value={kit} onChange={(e) => setKit(e.target.value)} />
        </Field>
        <Field label="Disposal allowance ($)" htmlFor={`d-${s.id}`}>
          <input id={`d-${s.id}`} inputMode="decimal" value={disposal} onChange={(e) => setDisposal(e.target.value)} />
        </Field>
        <Field label="Equipment allowance ($)" htmlFor={`e-${s.id}`}>
          <input id={`e-${s.id}`} inputMode="decimal" value={equipment} onChange={(e) => setEquipment(e.target.value)} />
        </Field>
        <Field label="Other direct cost ($)" htmlFor={`o-${s.id}`}>
          <input id={`o-${s.id}`} inputMode="decimal" value={other} onChange={(e) => setOther(e.target.value)} />
        </Field>
      </div>
      <fieldset className="trip">
        <legend>Trip cost (paid to the Maestro once per visit)</legend>
        <label>
          <input type="radio" name={`trip-${s.id}`} checked={mode === "global"} onChange={() => setMode("global")} />
          Use the global {money(settings.mobilization_cents)}
        </label>
        <label>
          <input type="radio" name={`trip-${s.id}`} checked={mode === "rides"} onChange={() => setMode("rides")} />
          Rides another visit ($0)
        </label>
        <label>
          <input type="radio" name={`trip-${s.id}`} checked={mode === "custom"} onChange={() => setMode("custom")} />
          Custom
          {mode === "custom" && (
            <input className="inline-input" inputMode="decimal" value={trip} onChange={(e) => setTrip(e.target.value)} aria-label="Custom trip cost in dollars" placeholder="$" />
          )}
        </label>
      </fieldset>
      <Field label="Where these numbers come from" htmlFor={`src-${s.id}`}>
        <input id={`src-${s.id}`} value={source} onChange={(e) => setSource(e.target.value)} placeholder="e.g. BOM template, vendor quote" />
      </Field>
      {c?.updated_by_email && <p className="hint">Last changed by {c.updated_by_email}.</p>}
      {invalid && <p className="hint warn">Every cost must be a dollar amount (trip cost up to $200).</p>}
      <button
        type="button"
        className="btn primary"
        disabled={!dirty || invalid}
        onClick={() => onPropose({ change, title: `${s.name}: costs` })}
      >
        Preview cost change
      </button>
    </section>
  );
}

// ── Availability ───────────────────────────────────────────
function AvailabilitySection({ s, onPropose }: { s: Service; onPropose: (p: Pending) => void }) {
  const [allYear, setAllYear] = useState(s.available_months === null);
  const [months, setMonths] = useState<number[]>(s.available_months ?? []);
  const [preOn, setPreOn] = useState(s.presale_months !== null);
  const [pre, setPre] = useState<number[]>(s.presale_months ?? []);
  const regionsOff = new Set<Region>(s.inactive_regions ?? []);
  const [pnw, setPnw] = useState(!regionsOff.has("pnw"));
  const [norcal, setNorcal] = useState(!regionsOff.has("norcal"));

  const nextMonths = allYear ? null : months;
  const nextPre = preOn ? pre : null;
  // Server stores regions sorted (norcal before pnw); compare in the same order.
  const nextOff: Region[] = [...(norcal ? [] : (["norcal"] as Region[])), ...(pnw ? [] : (["pnw"] as Region[]))];
  const same = (a: number[] | null, b: number[] | null) => JSON.stringify(a) === JSON.stringify(b);

  const change: Change = { type: "service_availability", service_id: s.id };
  if (!same(nextMonths, s.available_months)) change.available_months = nextMonths;
  if (!same(nextPre, s.presale_months)) change.presale_months = nextPre;
  if (JSON.stringify(nextOff) !== JSON.stringify([...(s.inactive_regions ?? [])].sort())) change.inactive_regions = nextOff;
  const dirty = Object.keys(change).length > 2;
  const invalid = (!allYear && months.length === 0) || (preOn && (pre.length === 0 || pre.length > 11)) || (preOn && allYear);

  return (
    <section className="editor-section">
      <SectionLabel>Availability</SectionLabel>
      <div className="toggle-row">
        <span>
          {s.active ? "On sale" : "Switched off"} — customers {s.active ? "can" : "can't"} book this service.
        </span>
        <button
          type="button"
          className="btn"
          onClick={() =>
            onPropose({
              change: { type: "service_active", service_id: s.id, active: !s.active },
              title: `${s.name}: switch ${s.active ? "off" : "on"}`,
            })
          }
        >
          {s.active ? "Preview switching off" : "Preview switching on"}
        </button>
      </div>

      <Field label="Delivery months">
        <label className="check">
          <input type="checkbox" checked={allYear} onChange={(e) => setAllYear(e.target.checked)} /> All year
        </label>
        {!allYear && <MonthToggles label="Delivery months" value={months} onChange={setMonths} />}
      </Field>
      <Field label="Pre-orders" hint="Pre-orders need a delivery window. Customers are charged at order.">
        <label className="check">
          <input type="checkbox" checked={preOn} onChange={(e) => setPreOn(e.target.checked)} /> Take pre-orders
        </label>
        {preOn && <MonthToggles label="Pre-order months" value={pre} onChange={setPre} />}
      </Field>
      <Field label="Sold in">
        <label className="check">
          <input type="checkbox" checked={pnw} onChange={(e) => setPnw(e.target.checked)} /> PNW
        </label>
        <label className="check">
          <input type="checkbox" checked={norcal} onChange={(e) => setNorcal(e.target.checked)} /> NorCal
        </label>
      </Field>
      {invalid && <p className="hint warn">Pick at least one delivery month; pre-orders need a delivery window and 1–11 months.</p>}
      <button
        type="button"
        className="btn primary"
        disabled={!dirty || invalid}
        onClick={() => onPropose({ change, title: `${s.name}: availability` })}
      >
        Preview availability change
      </button>
    </section>
  );
}

// ── Seasonality ────────────────────────────────────────────
function SeasonalitySection({ s, onPropose }: { s: Service; onPropose: (p: Pending) => void }) {
  return (
    <section className="editor-section">
      <SectionLabel>Seasonality</SectionLabel>
      <div className="toggle-row">
        <span>
          {s.seasonal_pricing
            ? "Price follows the monthly seasonal multipliers."
            : "Flat price all year; seasonal multipliers are ignored."}
        </span>
        <button
          type="button"
          className="btn"
          onClick={() =>
            onPropose({
              change: { type: "seasonal_exemption", service_id: s.id, seasonal_pricing: !s.seasonal_pricing },
              title: `${s.name}: ${s.seasonal_pricing ? "flat all year" : "follow seasonal multipliers"}`,
            })
          }
        >
          {s.seasonal_pricing ? "Preview flat pricing" : "Preview seasonal pricing"}
        </button>
      </div>
    </section>
  );
}

// ── What the worker sees (GM-TXT-02 B3): the scope line in offer texts ──
const PREVIEW_LANGS: { id: Lang; label: string }[] = [
  { id: "es_MX", label: "Tú" }, { id: "es_CA", label: "Vos" }, { id: "en", label: "English" },
];
function ScopeSection({ s, onPropose }: { s: Service; onPropose: (p: Pending) => void }) {
  const [es, setEs] = useState(s.scope_es ?? "");
  const [en, setEn] = useState(s.scope_en ?? "");
  const [lang, setLang] = useState<Lang>("es_MX");
  const esProblems = scopeProblems(es), enProblems = scopeProblems(en);
  const draft = isDraft(es) || isDraft(en);
  const norm = (v: string) => (v.trim() === "" ? null : v.trim());
  const change: { scope_es?: string | null; scope_en?: string | null } = {};
  if (norm(es) !== (s.scope_es ?? null)) change.scope_es = norm(es);
  if (norm(en) !== (s.scope_en ?? null)) change.scope_en = norm(en);
  const changed = Object.keys(change).length > 0;
  const text = offerPreview(s, lang, lang === "en" ? en : es);
  const seg = smsSegments(text);
  const verifying = draft === false && (isDraft(s.scope_es) || isDraft(s.scope_en));
  return (
    <section className="editor-section scope-section">
      <SectionLabel>What the worker sees</SectionLabel>
      <p className="framing">
        {!s.scope_es && !s.scope_en
          ? "No scope line yet — offers for this service leave out “Incluye:”."
          : "One line a worker reads on his phone to decide: what he does, what he hauls or pays, and the size. No prices."}
      </p>
      {draft && (
        <Notice kind="info">
          Claude draft. Workers don't see it until the “_” at the start is removed. Read it, fix it, then remove the mark.
        </Notice>
      )}
      <Field label="Español (tú y vos)" htmlFor={`scope-es-${s.id}`}
        hint={<span className={esProblems.length ? "hint warn" : "hint"}>{esProblems.length ? esProblems.join(" · ") : `${es.length}/${SCOPE_MAX}`}</span>}>
        <textarea id={`scope-es-${s.id}`} rows={3} value={es} onChange={(e) => setEs(e.target.value)} />
      </Field>
      <Field label="English" htmlFor={`scope-en-${s.id}`}
        hint={<span className={enProblems.length ? "hint warn" : "hint"}>{enProblems.length ? enProblems.join(" · ") : `${en.length}/${SCOPE_MAX}`}</span>}>
        <textarea id={`scope-en-${s.id}`} rows={3} value={en} onChange={(e) => setEn(e.target.value)} />
      </Field>
      {draft && (
        <button type="button" className="btn" onClick={() => { setEs(withoutMark(es)); setEn(withoutMark(en)); }}>
          Remove the “_” mark (verified)
        </button>
      )}
      <div className="scope-preview">
        <div className="segmented" role="group" aria-label="Preview language">
          {PREVIEW_LANGS.map((l) => (
            <button key={l.id} type="button" className={lang === l.id ? "seg on" : "seg"} aria-pressed={lang === l.id} onClick={() => setLang(l.id)}>
              {l.label}
            </button>
          ))}
        </div>
        <p className="preview-line scope-text">{text}</p>
        <p className="hint">
          {seg.segments} SMS {seg.segments === 1 ? "part" : "parts"} · {seg.units} characters ({seg.encoding}) · sample job in Everett 98203
          {draft ? " · how it will read once verified" : ""}
        </p>
      </div>
      <button
        type="button"
        className="btn primary"
        disabled={!changed || esProblems.length > 0 || enProblems.length > 0}
        onClick={() =>
          onPropose({
            change: { type: "service_scope", service_id: s.id, ...change },
            title: `${s.name}: ${verifying ? "scope line verified" : "what the worker sees"}`,
          })
        }
      >
        Preview scope change
      </button>
    </section>
  );
}

// ── Visit minimum (HSC-01): allow a service below the single-booking minimum ──
function MinimumSection({ s, settings, onPropose }: { s: Service; settings: Settings; onPropose: (p: Pending) => void }) {
  const current: MinVisitExempt | null = s.min_visit_exempt ?? null;
  const [choice, setChoice] = useState<MinVisitExempt | "">(current ?? "");
  const next: MinVisitExempt | null = choice === "" ? null : choice;
  const on = settings.single_visit_minimum_enabled ?? true;
  const min = settings.single_visit_minimum_cents ?? null;
  return (
    <section className="editor-section">
      <SectionLabel>Visit minimum</SectionLabel>
      <p className="framing">
        {!on
          ? "The single-booking minimum is off (Settings), so every booking goes through."
          : current
            ? `Allowed below the ${min !== null ? money(min) : ""} minimum — ${MIN_VISIT_EXEMPT_LABEL[current]}.`
            : `Single bookings under ${min !== null ? money(min) : "the minimum"} are refused at checkout.`}
      </p>
      <Field label="Allowed below the minimum" htmlFor={`minex-${s.id}`}>
        <select id={`minex-${s.id}`} value={choice} onChange={(e) => setChoice(e.target.value as MinVisitExempt | "")}>
          <option value="">No — minimum applies</option>
          {(Object.keys(MIN_VISIT_EXEMPT_LABEL) as MinVisitExempt[]).map((k) => (
            <option key={k} value={k}>Yes — {MIN_VISIT_EXEMPT_LABEL[k]}</option>
          ))}
        </select>
      </Field>
      <button
        type="button"
        className="btn primary"
        disabled={next === current}
        onClick={() =>
          onPropose({
            change: { type: "service_min_exempt", service_id: s.id, min_visit_exempt: next },
            title: `${s.name}: ${next ? "allowed below the minimum" : "minimum applies"}`,
          })
        }
      >
        Preview minimum change
      </button>
    </section>
  );
}

// ── Tracks (SR-B2: services priced by track, e.g. Christmas lights) ──
function TracksSection({ s, variants, settings, onPropose }: {
  s: Service; variants: ServiceVariant[]; settings: Settings; onPropose: (p: Pending) => void;
}) {
  const premium = settings.ladder_premium_cents_per_hr ?? 0;
  const minimum = settings.cart_visit_minimum_cents ?? null;
  return (
    <section className="editor-section">
      <SectionLabel>Tracks</SectionLabel>
      <p className="framing">
        Priced by track: each has its own price, hours and kit. Maestro pay = person-hours × (título rate
        {premium > 0 ? ` + ${money(premium)}/h ladder premium` : ""}) for the install and the January takedown, plus
        a {money(settings.mobilization_cents)} trip per visit shared by the whole order.
        {minimum !== null ? ` Orders start at ${money(minimum)}.` : " No visit minimum."} Premium and minimum are in Settings.
      </p>
      {variants.map((v) => (
        <TrackEditor key={v.variant} s={s} v={v} settings={settings} onPropose={onPropose} />
      ))}
    </section>
  );
}

function TrackEditor({ s, v, settings, onPropose }: {
  s: Service; v: ServiceVariant; settings: Settings; onPropose: (p: Pending) => void;
}) {
  const label = TRACK_LABEL[v.variant] ?? v.variant;
  const [price, setPrice] = useState(toDollarText(v.base_price_cents));
  const [kit, setKit] = useState(toDollarText(v.kit_cost_cents));
  const [ih, setIh] = useState(v.install_hours === null ? "" : String(v.install_hours));
  const [th, setTh] = useState(v.takedown_hours === null ? "" : String(v.takedown_hours));
  const [crew, setCrew] = useState(String(v.crew_size));

  const priceC = parseDollars(price);
  const kitC = parseDollars(kit);
  const ihN = ih.trim() === "" ? null : Number(ih);
  const thN = th.trim() === "" ? null : Number(th);
  const crewN = Number(crew);
  const okHours = (n: number | null) => n !== null && Number.isFinite(n) && n >= 0.25 && n <= 100;
  const invalid =
    priceC === null || priceC < 100 || kitC === null || !okHours(ihN) || !okHours(thN) ||
    !Number.isInteger(crewN) || crewN < 1 || crewN > 4;

  const change: Change = { type: "variant_item", service_id: s.id, variant: v.variant };
  if (!invalid) {
    if (priceC !== v.base_price_cents) change.base_price_cents = priceC!;
    if (kitC !== v.kit_cost_cents) change.kit_cost_cents = kitC!;
    if (ihN !== Number(v.install_hours)) change.install_hours = ihN!;
    if (thN !== Number(v.takedown_hours)) change.takedown_hours = thN!;
    if (crewN !== v.crew_size) change.crew_size = crewN;
  }
  const dirty = Object.keys(change).length > 3;
  const e = trackEconomics(v);
  const limits = [
    `up to ${v.max_qty} per order`,
    v.exclusive_group === "roofline" ? "one roofline option per order" : v.exclusive_group ? `one ${v.exclusive_group} option per order` : null,
    v.footage_ft ? `${v.footage_ft} ft of lights` : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className="track card">
      <div className="toggle-row">
        <strong>{label}</strong>
        <span className="muted">
          {v.prices.map((p) => `${p.zip} ${money(p.price_cents)}`).join(" · ")}
        </span>
      </div>
      <div className="fields">
        <Field label="Price ($)" htmlFor={`vp-${s.id}-${v.variant}`}>
          <input id={`vp-${s.id}-${v.variant}`} inputMode="decimal" value={price} onChange={(ev) => setPrice(ev.target.value)} />
        </Field>
        <Field label="Kit ($)" htmlFor={`vk-${s.id}-${v.variant}`} hint={v.variant === "new" ? "Lights and clips we ship." : "Usually $0 — the customer's lights."}>
          <input id={`vk-${s.id}-${v.variant}`} inputMode="decimal" value={kit} onChange={(ev) => setKit(ev.target.value)} />
        </Field>
        <Field label="Install hours" htmlFor={`vi-${s.id}-${v.variant}`} hint="Person-hours, whole crew.">
          <input id={`vi-${s.id}-${v.variant}`} inputMode="decimal" value={ih} onChange={(ev) => setIh(ev.target.value)} placeholder="Not set" />
        </Field>
        <Field label="Takedown hours" htmlFor={`vt-${s.id}-${v.variant}`} hint="January visit, person-hours.">
          <input id={`vt-${s.id}-${v.variant}`} inputMode="decimal" value={th} onChange={(ev) => setTh(ev.target.value)} placeholder="Not set" />
        </Field>
        <Field label="Crew size" htmlFor={`vc-${s.id}-${v.variant}`} hint="1–4 people.">
          <input id={`vc-${s.id}-${v.variant}`} inputMode="numeric" value={crew} onChange={(ev) => setCrew(ev.target.value)} />
        </Field>
      </div>
      {invalid && <p className="hint warn">Price at least $1; kit a dollar amount; hours between 0.25 and 100; crew 1–4.</p>}
      <button
        type="button"
        className="btn primary"
        disabled={!dirty || invalid}
        onClick={() => onPropose({ change, title: `${s.name}: ${label.toLowerCase()} track` })}
      >
        Preview {label.toLowerCase()} change
      </button>

      {e ? <TrackEconomicsTable e={e} settings={settings} /> : <p className="muted">No price yet, so there are no economics to show.</p>}
      <p className="hint">Cart: {limits}.</p>
    </div>
  );
}

function TrackEconomicsTable({ e, settings }: { e: NonNullable<ReturnType<typeof trackEconomics>>; settings: Settings }) {
  const t = e.track;
  const tiers: { key: Tier; label: string; rate: number }[] = [
    { key: "aprendiz", label: "Aprendiz", rate: settings.rate_titulo_aprendiz_cents },
    { key: "oficial", label: "Oficial", rate: settings.rate_titulo_oficial_cents },
    { key: "maestro", label: "Maestro", rate: settings.rate_titulo_maestro_cents },
  ];
  return (
    <>
      <div className="table-scroll">
        <table className="tier-table">
          <thead>
            <tr>
              <th scope="col">Título</th>
              <th scope="col">Maestro pay</th>
              <th scope="col">Margin</th>
              <th scope="col">Margin %</th>
              <th scope="col">Whole order</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((tier) => {
              const tone = marginTone(e.margin_pct[tier.key], (e.margin_cents[tier.key] ?? 0) < 0, settings.target_margin_pct, settings.margin_floor_pct);
              const alone = t?.alone_margin_pct[tier.key] ?? null;
              const aloneTone = marginTone(alone, (t?.alone_margin_cents[tier.key] ?? 0) < 0, settings.target_margin_pct, settings.margin_floor_pct);
              return (
                <tr key={tier.key}>
                  <th scope="row">
                    {tier.label} <span className="muted">{money(tier.rate)}/h</span>
                  </th>
                  <td className="money">
                    {money(e.payout_cents[tier.key])}
                    {t && t.install_pay_cents[tier.key] !== null && (
                      <span className="muted"> ({money(t.install_pay_cents[tier.key])} + {money(t.takedown_pay_cents[tier.key])})</span>
                    )}
                  </td>
                  <td className="money">{money(e.margin_cents[tier.key])}</td>
                  <td>{e.margin_pct[tier.key] === null ? <span className="muted">—</span> : <Margin value={pct(e.margin_pct[tier.key])} tone={tone} estimated={e.estimated} />}</td>
                  <td>{alone === null ? <span className="muted">—</span> : <Margin value={pct(alone)} tone={aloneTone} estimated={e.estimated} />}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="hint">
        At {money(e.price_cents)}: card fee {money(e.fee_cents)}, kit {money(e.kit_cents)}.{" "}
        {t && t.install_hours !== null && (
          <>
            Pay = install {t.install_hours} h + takedown {t.takedown_hours} h
            {t.crew_size > 1 ? ` (${t.crew_size}-person crew, person-hours)` : ""} × (título rate
            {t.ladder_premium_cents_per_hr > 0 ? ` + ${money(t.ladder_premium_cents_per_hr)}/h ladder premium` : ""}), shown as install + takedown.{" "}
          </>
        )}
        {e.basis === "variant_no_hours" && "Hours aren't set for this track, so pay and margin can't be computed. "}
        Margin is this item's share; each visit also pays one {money(t?.trip_cents ?? settings.mobilization_cents)} trip,
        shared by everything in the order. "Whole order" is this item booked alone: both trips, install and January takedown.
      </p>
    </>
  );
}

// ── Tier economics (read-only) ─────────────────────────────
function TierSection({ s, settings }: { s: Service; settings: Settings }) {
  const e = primaryEconomics(s);
  const zip = s.prices[0]?.zip;
  if (!e) {
    return (
      <section className="editor-section">
        <SectionLabel>Pay by título</SectionLabel>
        <p className="muted">No price yet, so there are no economics to show.</p>
      </section>
    );
  }
  const tiers: { key: Tier; label: string; rate: number }[] = [
    { key: "aprendiz", label: "Aprendiz", rate: settings.rate_titulo_aprendiz_cents },
    { key: "oficial", label: "Oficial", rate: settings.rate_titulo_oficial_cents },
    { key: "maestro", label: "Maestro", rate: settings.rate_titulo_maestro_cents },
  ];
  return (
    <section className="editor-section">
      <SectionLabel>Pay by título</SectionLabel>
      <p className="framing">Price is fixed; your margin depends on who takes the job.</p>
      <div className="table-scroll">
        <table className="tier-table">
          <thead>
            <tr>
              <th scope="col">Título</th>
              <th scope="col">Payout</th>
              <th scope="col">Margin</th>
              <th scope="col">Margin %</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((t) => {
              const tone = marginTone(e.margin_pct[t.key], (e.margin_cents[t.key] ?? 0) < 0, settings.target_margin_pct, settings.margin_floor_pct);
              return (
                <tr key={t.key}>
                  <th scope="row">
                    {t.label} <span className="muted">{money(t.rate)}/h</span>
                  </th>
                  <td className="money">{money(e.payout_cents[t.key])}</td>
                  <td className="money">{money(e.margin_cents[t.key])}</td>
                  <td>{e.margin_pct[t.key] === null ? <span className="muted">—</span> : <Margin value={pct(e.margin_pct[t.key])} tone={tone} estimated={e.estimated} />}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="hint">
        At {zip}, {money(e.price_cents)}: card fee {money(e.fee_cents)}, kit {money(e.kit_cents)}, other {money(e.other_cents)}, allowances{" "}
        {money(e.allowance_cents)}, trip {money(e.mobilization_cents)}.{" "}
        {e.basis === "hours" && "Payout uses standard hours × título rate."}
        {e.basis === "legacy_rate" && "Payout uses the legacy contractor rate, so the Aprendiz and Maestro rates aren't computed."}
        {e.basis === "estimated_44" && "No cost profile: margin is an estimate at a flat 44% and isn't real."}
      </p>
    </section>
  );
}
